import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  AccountTree as RelationshipIcon,
  Link as LinkIcon,
  LinkOff as UnlinkIcon,
  AutoFixHigh as AutoDetectIcon,
  CheckCircle as MatchIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { Document, DocumentRelationship } from '../../features/cross-document/documentSetService';
import { tierOpacity } from './tierOpacity';

// Visual graph component for relationships (lazy-loaded to avoid import-time crashes)
const ForceGraph2D = React.lazy(() => import('react-force-graph-2d'));

interface RelationshipBuilderProps {
  documents: Document[];
  relationships: DocumentRelationship[];
  onRelationshipCreate?: (rel: DocumentRelationship) => void;
  onRelationshipDelete?: (relId: string) => void;
  onAutoDetect?: () => Promise<void>;
  isLoading?: boolean;
}

interface GraphData {
  nodes: Array<{
    id: string;
    name: string;
    type?: string;
    color: string;
  }>;
  links: Array<{
    source: string;
    target: string;
    type: string;
    confidence: number;
    tier?: 'strong' | 'medium' | 'weak';
  }>;
}

const RELATIONSHIP_COLORS: Record<string, string> = {
  invoice_reference: '#4CAF50',
  shipment_reference: '#2196F3',
  party_match: '#FF9800',
  date_match: '#9C27B0',
  product_match: '#00BCD4',
  container_match: '#795548',
  default: '#607D8B',
};

const CONFIDENCE_THRESHOLDS = {
  HIGH: 0.9,
  MEDIUM: 0.7,
  LOW: 0.5,
};

export const RelationshipBuilder: React.FC<RelationshipBuilderProps> = ({
  documents,
  relationships,
  onRelationshipCreate,
  onRelationshipDelete,
  onAutoDetect,
  isLoading = false,
}) => {
  const [graphData, setGraphData] = useState<GraphData>({ nodes: [], links: [] });
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [hoveredLink, setHoveredLink] = useState<DocumentRelationship | null>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const graphRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Measure container and auto-fit graph when data changes
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setContainerSize({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Zoom to fit all nodes after the simulation stabilizes
  const handleEngineStop = useCallback(() => {
    try {
      graphRef.current?.zoomToFit?.(300, 40);
    } catch { /* graph not ready */ }
  }, []);

  // Convert data to graph format
  useEffect(() => {
    const nodes = documents.map((doc) => ({
      id: doc.id,
      name: doc.file_name,
      type: doc.document_type,
      color: getDocumentColor(doc.document_type),
    }));

    const nodeIds = new Set(nodes.map((n) => String(n.id)));
    const links = relationships
      .filter((rel) => nodeIds.has(String(rel.document_a_id)) && nodeIds.has(String(rel.document_b_id)))
      .map((rel) => ({
        source: String(rel.document_a_id),
        target: String(rel.document_b_id),
        type: rel.relationship_type,
        confidence: rel.confidence,
        tier: rel.tier,
      }));

    setGraphData({ nodes, links });

    // Reheat simulation and zoom to fit after new data settles
    const reheatTimer = setTimeout(() => {
      try {
        graphRef.current?.d3ReheatSimulation?.();
        graphRef.current?.zoomToFit?.(400, 40);
      } catch { /* graph not ready */ }
    }, 500);
    return () => clearTimeout(reheatTimer);
  }, [documents, relationships]);

  const getDocumentColor = (type?: string) => {
    if (!type) return '#9E9E9E';
    const colors: Record<string, string> = {
      commercial_invoice: '#4CAF50',
      bill_of_lading: '#2196F3',
      packing_list: '#FF9800',
      certificate_of_origin: '#9C27B0',
      default: '#607D8B',
    };
    return colors[type] || colors.default;
  };

  const getRelationshipColor = (type: string) => {
    return RELATIONSHIP_COLORS[type] || RELATIONSHIP_COLORS.default;
  };

  const getConfidenceLevel = (confidence: number) => {
    if (confidence >= CONFIDENCE_THRESHOLDS.HIGH) return 'high';
    if (confidence >= CONFIDENCE_THRESHOLDS.MEDIUM) return 'medium';
    if (confidence >= CONFIDENCE_THRESHOLDS.LOW) return 'low';
    return 'very-low';
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= CONFIDENCE_THRESHOLDS.HIGH) return '#4CAF50';
    if (confidence >= CONFIDENCE_THRESHOLDS.MEDIUM) return '#FF9800';
    if (confidence >= CONFIDENCE_THRESHOLDS.LOW) return '#F44336';
    return '#9E9E9E';
  };

  const formatRelationshipType = (type: string) => {
    return type
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const hexToRgba = (hex: string, alpha: number): string => {
    const h = hex.replace('#', '');
    const r = parseInt(h.length === 3 ? h[0] + h[0] : h.substring(0, 2), 16);
    const g = parseInt(h.length === 3 ? h[1] + h[1] : h.substring(2, 4), 16);
    const b = parseInt(h.length === 3 ? h[2] + h[2] : h.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Group relationships by type
  const relationshipsByType = relationships.reduce((acc, rel) => {
    const type = rel.relationship_type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(rel);
    return acc;
  }, {} as Record<string, DocumentRelationship[]>);

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <RelationshipIcon color="primary" />
            <Typography variant="h6">Document Relationships</Typography>
            <Chip
              label={`${relationships.length} connections`}
              size="small"
              color="primary"
            />
          </Box>
          {onAutoDetect && (
            <Tooltip title="Auto-detect relationships using AI">
              <IconButton
                onClick={onAutoDetect}
                disabled={isLoading || documents.length < 2}
                color="primary"
              >
                <AutoDetectIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Paper>

      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Graph Visualization */}
      {documents.length > 1 && (
        <Paper sx={{ p: 2, mb: 2, minHeight: 400, position: 'relative', overflow: 'hidden' }}>
          <Typography variant="subtitle1" gutterBottom>
            Relationship Graph
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Faded links are lower-confidence (weak) matches.
          </Typography>
          <Box
            ref={containerRef}
            sx={{
              height: 350,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            {containerSize.width > 0 && (
              <React.Suspense fallback={<LinearProgress />}>
              <ForceGraph2D
                key={`graph-${graphData.nodes.length}-${graphData.links.length}`}
                ref={graphRef}
                graphData={graphData}
                width={containerSize.width}
                height={containerSize.height || 350}
                nodeLabel="name"
                nodeColor="color"
                linkColor={(link: any) => {
                  try { return hexToRgba(getRelationshipColor(link?.type), tierOpacity(link?.tier, link?.confidence)); } catch { return '#607D8B'; }
                }}
                linkWidth={(link: any) => {
                  try { return Math.max(1, (link?.confidence ?? 0.5) * 5); } catch { return 1; }
                }}
                linkDirectionalParticles={2}
                linkDirectionalParticleSpeed={(link: any) => {
                  try { return (link?.confidence ?? 0.5) * 0.01; } catch { return 0.005; }
                }}
                cooldownTicks={80}
                onEngineStop={handleEngineStop}
                d3AlphaDecay={0.05}
                d3VelocityDecay={0.3}
                nodeCanvasObject={(node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
                  try {
                    if (node.x == null || node.y == null || !isFinite(node.x) || !isFinite(node.y)) return;
                    const label = node.name || '';
                    const fontSize = Math.max(1, 12 / (globalScale || 1));
                    ctx.font = `${fontSize}px Sans-Serif`;
                    const textWidth = ctx.measureText(label).width;
                    const bckgDimensions = [textWidth, fontSize].map((n) => n + fontSize * 0.2);

                    // Draw node circle
                    ctx.fillStyle = node.color || '#9E9E9E';
                    ctx.beginPath();
                    ctx.arc(node.x, node.y, 5, 0, 2 * Math.PI, false);
                    ctx.fill();

                    // Draw label background
                    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
                    ctx.fillRect(
                      node.x - bckgDimensions[0] / 2,
                      node.y + 6,
                      bckgDimensions[0],
                      bckgDimensions[1]
                    );

                    // Draw label below node
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'top';
                    ctx.fillStyle = 'black';
                    ctx.fillText(label, node.x, node.y + 6);
                  } catch {
                    // Silently skip frame if canvas drawing fails
                  }
                }}
                onNodeClick={(node: any) => {
                  try { setSelectedNode(node?.id); } catch { /* ignore */ }
                }}
                onLinkHover={(link: any) => {
                  try {
                    if (link) {
                      const sourceId = typeof link.source === 'object' ? link.source?.id : link.source;
                      const targetId = typeof link.target === 'object' ? link.target?.id : link.target;
                      const rel = relationships.find(
                        (r) =>
                          (r.document_a_id === sourceId && r.document_b_id === targetId) ||
                          (r.document_b_id === sourceId && r.document_a_id === targetId)
                      );
                      setHoveredLink(rel || null);
                    } else {
                      setHoveredLink(null);
                    }
                  } catch {
                    setHoveredLink(null);
                  }
                }}
              />
              </React.Suspense>
            )}
          </Box>
          {hoveredLink && (
            <Card
              sx={{
                position: 'absolute',
                top: 60,
                right: 10,
                width: 250,
                zIndex: 1000,
              }}
            >
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  {formatRelationshipType(hoveredLink.relationship_type)}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="caption" color="text.secondary">
                    Confidence:
                  </Typography>
                  <Chip
                    label={`${Math.round(hoveredLink.confidence * 100)}%`}
                    size="small"
                    sx={{
                      backgroundColor: getConfidenceColor(hoveredLink.confidence),
                      color: 'white',
                    }}
                  />
                </Box>
                {(hoveredLink.matching_fields || []).length > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      Matching Fields:
                    </Typography>
                    <Box mt={0.5}>
                      {(hoveredLink.matching_fields || []).map((field, idx) => (
                        <Chip
                          key={idx}
                          label={field}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </Paper>
      )}

      {/* Relationship Details */}
      {Object.keys(relationshipsByType).length > 0 ? (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Detected Relationships
          </Typography>
          {Object.entries(relationshipsByType).map(([type, rels], index) => (
            <Box key={type} mb={2}>
              {index > 0 && <Divider sx={{ my: 2 }} />}
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <LinkIcon sx={{ color: getRelationshipColor(type) }} />
                <Typography variant="subtitle2">
                  {formatRelationshipType(type)}
                </Typography>
                <Chip label={rels.length} size="small" />
              </Box>
              {rels.map((rel) => {
                const docA = documents.find((d) => d.id === rel.document_a_id);
                const docB = documents.find((d) => d.id === rel.document_b_id);
                const confidenceLevel = getConfidenceLevel(rel.confidence);

                return (
                  <Box
                    key={rel.id || `${rel.document_a_id}-${rel.document_b_id}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      mb: 1,
                      backgroundColor: 'background.default',
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <MatchIcon
                      sx={{
                        color: getConfidenceColor(rel.confidence),
                        fontSize: 20,
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      <strong>{docA?.file_name}</strong>
                      {' ↔ '}
                      <strong>{docB?.file_name}</strong>
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      {(rel.matching_fields || []).slice(0, 3).map((field, idx) => (
                        <Chip
                          key={idx}
                          label={field}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))}
                      {(rel.matching_fields || []).length > 3 && (
                        <Chip
                          label={`+${(rel.matching_fields || []).length - 3}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                    <Tooltip title={`Confidence: ${Math.round(rel.confidence * 100)}%`}>
                      <Chip
                        label={confidenceLevel}
                        size="small"
                        sx={{
                          backgroundColor: getConfidenceColor(rel.confidence),
                          color: 'white',
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                        }}
                      />
                    </Tooltip>
                    {onRelationshipDelete && (
                      <Tooltip title="Remove relationship">
                        <IconButton
                          size="small"
                          onClick={() => onRelationshipDelete(rel.id)}
                        >
                          <UnlinkIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Paper>
      ) : (
        <Alert severity="info">
          {documents.length < 2
            ? 'Add at least 2 documents to detect relationships'
            : 'No relationships detected. Try auto-detection or add documents with matching data.'}
        </Alert>
      )}

      {/* Warnings for low confidence relationships */}
      {relationships.some((r) => r.confidence < CONFIDENCE_THRESHOLDS.LOW) && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon />
            <Typography variant="body2">
              Some relationships have low confidence scores. Please review and validate before
              submission.
            </Typography>
          </Box>
        </Alert>
      )}
    </Box>
  );
};

export default RelationshipBuilder;